<template>
  <div class="request-information-view">
    <header class="request-information-view__header">
      <h1 class="h2 request-information-view__headline">
        {{ $t("depotfinder.results.request-information.headline") }}
      </h1>
      <button
        class="btn btn--icon icon-left btn--small btn--secondary btn--outline"
        @click="goBack()"
      >
        {{ $t("global.back") }}
        <svg class="icon icon--arrow-left" width="18" height="18">
          <use xlink:href="#icon-arrow-left" />
        </svg>
      </button>
    </header>
    <div class="request-information-view__content">
      <p class="request-information-view__subline">
        {{ $t("depotfinder.results.request-information.subline") }}
      </p>
      <div class="request-information">
        <ValidationObserver
          v-if="!formSuccess"
          v-slot="{ invalid }"
          tag="form"
          @submit.prevent="validateForm"
          ref="observer"
        >
          <div class="request-information__grid flex-grid grid-pad">
            <div class="flex-grid__col c-5">
              <div class="input-block">
                <span class="label">{{
                  $t("depotfinder.results.request-information.label-salutation")
                }}</span>
                <ValidationProvider
                  rules="required"
                  v-slot="{ classes }"
                  tag="div"
                  class="radio-buttons"
                >
                  <div
                    class="radio radio--single"
                    v-for="(salutation, index) in salutations"
                    :key="index"
                  >
                    <input
                      :id="'salutation-' + index"
                      type="radio"
                      name="salutation"
                      :value="salutation"
                      v-model="form.salutation"
                      :class="classes"
                    />
                    <label :for="'salutation-' + index">
                      <span class="radio__checkmark"></span>
                      <p class="radio__label" v-html="salutation"></p>
                    </label>
                  </div>
                </ValidationProvider>
              </div>
              <div class="input-block">
                <label class="label" for="input-firstname">{{
                  $t("depotfinder.results.request-information.label-name")
                }}</label>
                <div class="flex-grid grid-pad grid-pad--narrow">
                  <div class="flex-grid__col c-6">
                    <ValidationProvider rules="required" v-slot="{ classes }">
                      <input
                        type="text"
                        id="input-firstname"
                        name="firstname"
                        v-model="form.firstname"
                        :class="classes"
                        :placeholder="
                          $t('depotfinder.results.request-information.name')
                        "
                      />
                    </ValidationProvider>
                  </div>
                  <div class="flex-grid__col c-6">
                    <ValidationProvider rules="required" v-slot="{ classes }">
                      <input
                        type="text"
                        id="input-surname"
                        name="surname"
                        v-model="form.surname"
                        :class="classes"
                        :placeholder="
                          $t('depotfinder.results.request-information.surname')
                        "
                      />
                    </ValidationProvider>
                  </div>
                </div>
              </div>
              <div class="input-block">
                <label class="label" for="input-contact">{{
                  $t("depotfinder.results.request-information.label-contact")
                }}</label>
                <div class="flex-grid grid-pad grid-pad--narrow">
                  <div class="flex-grid__col c-6">
                    <div class="select">
                      <span class="select__box">
                        <select
                          id="input-contact"
                          v-model="contactMethod"
                          name="contact"
                        >
                          <option value="phone">{{
                            $t("depotfinder.results.request-information.phone")
                          }}</option>
                          <option value="email">{{
                            $t("depotfinder.results.request-information.email")
                          }}</option>
                        </select>
                      </span>
                    </div>
                  </div>
                  <div class="flex-grid__col c-6">
                    <ValidationProvider
                      :rules="contactMethod == 'phone' ? 'required' : ''"
                      v-slot="{ classes }"
                    >
                      <input
                        v-if="contactMethod == 'phone'"
                        type="text"
                        inputmode="tel"
                        id="input-phone"
                        v-model="form.phone"
                        :class="classes"
                        :placeholder="
                          $t(
                            'depotfinder.results.request-information.phonenumber'
                          )
                        "
                      />
                    </ValidationProvider>
                    <ValidationProvider
                      :rules="contactMethod == 'email' ? 'required|email' : ''"
                      v-slot="{ classes }"
                    >
                      <input
                        v-if="contactMethod == 'email'"
                        type="email"
                        id="input-email"
                        v-model="form.email"
                        :class="classes"
                        :placeholder="
                          $t('depotfinder.results.request-information.email')
                        "
                      />
                    </ValidationProvider>
                  </div>
                </div>
              </div>
              <div class="form-error" v-if="sendError">
                <p class="small-text">
                  {{ $t("depotfinder.results.request-information.form-error") }}
                </p>
              </div>
            </div>
            <div class="flex-grid__col c-7">
              <div class="input-block">
                <span class="label">{{
                  $t(
                    "depotfinder.results.request-information.label-interest-models"
                  )
                }}</span>
                <div class="modelselect-wrap">
                  <div
                    class="modelselect"
                    v-for="(model, index) in depositModels"
                    :key="index"
                    :ref="'toggleCheckbox' + index"
                  >
                    <div
                      class="checkbox checkbox--boxed modelselect__inner"
                      :class="{
                        'is-checked':
                          Object.values(selectedModels).indexOf(index + '50') !=
                            -1 ||
                          Object.values(selectedModels).indexOf(
                            index + '100'
                          ) != -1
                      }"
                    >
                      <input
                        type="checkbox"
                        :id="'modelselect-' + index"
                        :value="index"
                        v-model="selectedModels"
                      />
                      <label :for="'modelselect-' + index">
                        <div class="deposit-model">
                          <div class="deposit-model__icon">
                            <svg width="64" height="64">
                              <use
                                v-bind:xlink:href="getDepotsitModelIcon(index)"
                              />
                            </svg>
                          </div>
                          <span class="deposit-model__name">{{
                            model.name
                          }}</span>
                          <!--<span class="deposit-model__tarrifs">{{ selectedModels }}</span>-->
                          <svg
                            v-if="index == recommendedDepositModel"
                            class="modelselect__star"
                            width="20"
                            height="19"
                          >
                            <use xlink:href="#icon-star" />
                          </svg>
                        </div>
                      </label>
                      <div class="tarrif-checkboxes checkboxes">
                        <div
                          class="checkbox"
                          v-if="getDepotsitModelTarrifs(index)"
                        >
                          <input
                            type="checkbox"
                            :id="'modelselect-' + index"
                            :value="index"
                            v-model="selectedModels"
                          />
                          <label :for="'modelselect-' + index">{{
                            model.name
                          }}</label>
                        </div>
                        <div
                          class="checkbox"
                          v-for="(tarrif, index) in getDepotsitModelTarrifs(
                            index
                          )"
                          :key="index"
                        >
                          <input
                            type="checkbox"
                            :id="'tarrifselect-' + tarrif"
                            :value="tarrif"
                            v-model="selectedModels"
                          />
                          <label :for="'tarrifselect-' + tarrif">{{
                            depositModelTarrifs[tarrif].name
                          }}</label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="input-block">
                <span class="label">{{
                  $t(
                    "depotfinder.results.request-information.label-interest-assets"
                  )
                }}</span>
                <div class="modelselect-wrap">
                  <div
                    class="modelselect modelselect--small"
                    v-for="(model, index) in specialDepositModels"
                    :key="index"
                  >
                    <div class="checkbox checkbox--boxed modelselect__inner">
                      <input
                        type="checkbox"
                        :id="'modelselect-' + index"
                        :value="index"
                        v-model="selectedSpecialModels"
                      />
                      <label :for="'modelselect-' + index">
                        <div class="deposit-model">
                          <div class="deposit-model__icon">
                            <svg width="64" height="64">
                              <use
                                v-bind:xlink:href="getDepotsitModelIcon(index)"
                              />
                            </svg>
                          </div>
                          <span class="deposit-model__name">{{
                            model.name
                          }}</span>
                          <svg
                            v-if="index == activeDepositModel"
                            class="modelselect__star"
                            width="20"
                            height="19"
                          >
                            <use xlink:href="#icon-star" />
                          </svg>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <UiButton theme="primary" size="medium" type="submit">
            {{ $t("depotfinder.results.request-information.submit") }}
          </UiButton>
        </ValidationObserver>
        <div v-if="formSuccess" class="form-success">

          <UiHeadline tag="h3" theme="primary" class="mb-1">
            {{
              $t(
                "depotfinder.results.request-information.form-success.headline"
              )
            }}
          </UiHeadline>
          <p class="form-success__message">
            {{
              $t("depotfinder.results.request-information.form-success.message")
            }}
          </p>
          <UiButton
            class="icon-left"
            theme="primary"
            size="small"
            icon="icon-arrow-left"
            @click.native="goBack()"
          >
            {{ $t("global.back") }}
          </UiButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
function initialFormState() {
  return {
    salutation: "",
    firstname: "",
    surname: "",
    email: "",
    phone: ""
  };
}

import UiHeadline from "./../components/Ui/Headline.vue";
import UiButton from "./../components/Ui/Button/index.vue";

export default {
  components: {
    UiButton,
    UiHeadline
  },
  props: {
    activeDepositModel: {
      type: String,
      required: true
    },
    recommendedDepositModel: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      depositModels: {
        DepotDirekt: this.$t("depositmodels.DepotDirekt"),
        DepotKomfort: this.$t("depositmodels.DepotKomfort"),
        DepotExklusiv: this.$t("depositmodels.DepotExklusiv"),
        DepotFuture: this.$t("depositmodels.DepotFuture")
      },
      specialDepositModels: {
        MeinVermögen: this.$t("depositmodels.MeinVermögen"),
        Vermögensverwaltung: this.$t("depositmodels.Vermögensverwaltung")
      },
      depositModelTarrifs: {
        DepotExklusiv50: this.$t("depositmodels.DepotExklusiv50"),
        DepotExklusiv100: this.$t("depositmodels.DepotExklusiv100")
      },
      salutations: this.$t(
        "depotfinder.results.request-information.salutations"
      ),
      contactMethod: "phone",
      form: initialFormState(),
      sendError: false
    };
  },
  computed: {
    selectedModels: {
      get() {
        return this.$store.getters.getSelectedModelsInformationState;
      },
      set(value) {
        this.$store.dispatch("updateInformationState", {
          index: "selectedModels",
          payload: value
        });
      }
    },
    selectedSpecialModels: {
      get() {
        return this.$store.getters.getSelectedSpecialModelsInformationState;
      },
      set(value) {
        this.$store.dispatch("updateInformationState", {
          index: "selectedSpecialModels",
          payload: value
        });
      }
    },
    formSuccess() {
      return this.$store.getters.getFormSuccessState;
    }
  },
  methods: {
    getDepotsitModelIcon(model) {
      return "#icon-" + model.toLowerCase();
    },
    getDepotsitModelTarrifs(model) {
      return this.depositModels[model]["tarrifs"];
    },
    goBack() {
      this.$store.dispatch("updateView", {
        view: "informationView",
        payload: false
      });
      this.sendError = false;
    },
    resetFormData() {
      Object.assign(this.form, initialFormState());
    },
    async validateForm() {
      const isValid = await this.$refs.observer.validate();

      if (isValid) {
        this.submitForm();
      }
    },
    submitForm() {
      const self = this;
      self.sendError = false; // reset form error on submit

      let message = "",
        salutation = this.form.salutation,
        firstname = this.form.firstname,
        surname = this.form.surname,
        contact = this.form[this.contactMethod],
        depositmodels = this.selectedModels.join(", "),
        specialdepositmodels = this.selectedSpecialModels.join(", "),
        nl = "\n";

      // Build message
      message +=
        "<b>Von:</b> " + salutation + " " + firstname + " " + surname + nl;
      message += "<b>Kontakt:</b> " + contact + nl + nl;

      if (depositmodels != "") {
        message += "<b>Ich habe Interesse an …</b>" + nl;
        message += depositmodels + nl + nl;
      }

      if (specialdepositmodels != "") {
        message +=
          "<b>Im Bereich Vermögensverwaltung interessiere ich mich für …</b>" +
          nl;
        message += specialdepositmodels;
      }

      if (depositmodels == "" && specialdepositmodels == "") {
        message += "Es wurden keine Interessen angegeben.";
      }

      let axiosConfig = {
        params: {
          message: message
        }
      };

      let urlPrefix = "";
      if (process.env.NODE_ENV !== "production") {
        urlPrefix = "http://localhost:5000";
        axiosConfig.params.is_dev = true;
      }

      let url = urlPrefix + "/api/form.php";
      this.axios
        .get(url, axiosConfig)
        .then(function() {
          self.$store.dispatch("updateView", {
            view: "formSuccess",
            payload: true
          });
          self.resetFormData();
        })
        .catch(function() {
          self.sendError = true;
        });
    }
  }
};
</script>
